/**
 * Firestore access for the events module.
 *
 * The whole set is small — 25 events, a handful of people — so it loads whole
 * and scores in the browser. The spec asks for a nightly scheduled function to
 * own the derived fields; until that exists, scoring here keeps the numbers
 * live as assignments change, and writes nothing back. That distinction
 * matters: `scoreEvents` is a read-time projection, so nothing can drift out of
 * step with the inputs, and no human edit can be silently overwritten.
 */
import {
  collection,
  deleteDoc,
  doc,
  onSnapshot,
  setDoc,
  type DocumentData,
  type QueryDocumentSnapshot,
} from 'firebase/firestore'
import { db } from '@/shared/firebase/client'
import { toIsoOrNull } from '@/shared/util/dates'
import {
  coverageFor,
  scoreEvent,
  type CrmEvent,
  type EventAssignment,
  type EventStage,
  type Person,
} from '@/types/events'

export const EVENT_COLLECTIONS = {
  events: 'skidaCrmEvents',
  assignments: 'skidaCrmEventAssignments',
  people: 'skidaCrmPeople',
} as const

/** A number, or null when the field has never been filled in. */
const numOrNull = (v: unknown): number | null =>
  typeof v === 'number' && Number.isFinite(v) ? v : null

const str = (v: unknown) => (typeof v === 'string' ? v : '')
const num = (v: unknown) => (typeof v === 'number' && Number.isFinite(v) ? v : null)
const list = (v: unknown) => (Array.isArray(v) ? v.map(String) : [])

function mapEvent(d: QueryDocumentSnapshot<DocumentData>): CrmEvent {
  const v = d.data()
  return {
    // Written by scripts/geocodeAll.ts and by nothing else, so it is passed
    // through as stored — precision included, which is the half that matters.
    // Spread in, never `undefined` — see the note in `partnersRepository`.
    ...(d.get('geo') ? { geo: d.get('geo') as CrmEvent['geo'] } : {}),
    id: d.id,
    name: str(v.name),
    category: str(v.category),
    country: str(v.country),
    region: str(v.region),
    venue: str(v.venue),
    startDate: str(v.startDate),
    endDate: str(v.endDate),
    dateConfidence: str(v.dateConfidence),
    audience: str(v.audience),
    website: str(v.website),
    instagram: str(v.instagram).replace(/^@/, ''),
    stage: (str(v.stage) || 'unqualified') as EventStage,
    importStage: str(v.importStage),
    ownerId: str(v.ownerId),
    ownerName: str(v.ownerName),
    outreachBy: str(v.outreachBy),
    trackingCode: str(v.trackingCode),
    budgetNok: numOrNull(v.budgetNok),
    actualSpendNok: numOrNull(v.actualSpendNok),
    registered: numOrNull(v.registered),
    attendees: numOrNull(v.attendees),
    sourceVerified: str(v.sourceVerified),
    notes: str(v.notes),
    nextAction: str(v.nextAction),
    contactName: str(v.contactName),
    contactEmail: str(v.contactEmail),
    contactPhone: str(v.contactPhone),
    costTier: str(v.costTier),
    sponsorshipModel: str(v.sponsorshipModel),
    lat: num(v.lat),
    lng: num(v.lng),
    relevance: num(v.relevance),
    reach: num(v.reach),
    coverage: num(v.coverage),
    eventScore: num(v.eventScore),
    tier: (str(v.tier) || null) as CrmEvent['tier'],
    costEstimateEur: num(v.costEstimateEur),
    scorePerKEur: num(v.scorePerKEur),
    scoredAt: str(v.scoredAt),
  }
}

function mapAssignment(d: QueryDocumentSnapshot<DocumentData>): EventAssignment {
  const v = d.data()
  return {
    id: d.id,
    eventId: str(v.eventId),
    personId: str(v.personId),
    personName: str(v.personName),
    role: (str(v.role) || 'ambassador') as EventAssignment['role'],
    status: (str(v.status) || 'proposed') as EventAssignment['status'],
    travelFrom: str(v.travelFrom),
    costEur: num(v.costEur),
    tasks: list(v.tasks),
    debrief: str(v.debrief),
    leadsCollected: num(v.leadsCollected),
  }
}

function mapPerson(d: QueryDocumentSnapshot<DocumentData>): Person {
  const v = d.data()
  const home = (v.homeBase ?? {}) as DocumentData
  return {
    // Written by scripts/geocodeAll.ts and by nothing else, so it is passed
    // through as stored — precision included, which is the half that matters.
    ...(d.get('geo') ? { geo: d.get('geo') as Person['geo'] } : {}),
    id: d.id,
    name: str(v.name),
    email: str(v.email),
    phone: str(v.phone),
    homeBase: { lat: num(home.lat), lng: num(home.lng), label: str(home.label) },
    countries: list(v.countries),
    maxTravelKm: num(v.maxTravelKm),
    languages: list(v.languages),
    blackoutDates: Array.isArray(v.blackoutDates)
      ? (v.blackoutDates as DocumentData[]).map((b) => ({ from: str(b.from), to: str(b.to) }))
      : [],
    active: v.active !== false,
    linkedLeadId: str(v.linkedLeadId),
    role:
      str(v.role) === 'pilot' ? 'pilot' : str(v.role) === 'staff' ? 'staff' : 'ambassador',
    homeArea: str(v.homeArea),

    postalAddress: str(v.postalAddress),
    tshirt: str(v.tshirt),
    oneLiner: str(v.oneLiner),
    website: str(v.website),
    source: str(v.source),

    // `status` postdates the import, so absent means active — the records that
    // exist were all created by somebody joining, not leaving. Which is also
    // why an unreadable value falls back to active rather than to prospect:
    // demoting a signed ambassador to a prospect would be a claim, not a default.
    status:
      str(v.status) === 'prospect'
        ? 'prospect'
        : str(v.status) === 'paused'
          ? 'paused'
          : str(v.status) === 'ended'
            ? 'ended'
            : 'active',
    compensation: str(v.compensation),
    campaignCode: str(v.campaignCode),
    contentDelivered: str(v.contentDelivered),
    driveFolderUrl: str(v.driveFolderUrl),

    instagram: str(v.instagram),
    facebook: str(v.facebook),
    tiktok: str(v.tiktok),
    youtube: str(v.youtube),
    linkedin: str(v.linkedin),
    instagramFollowers: num(v.instagramFollowers),
    instagramBio: str(v.instagramBio),
    instagramWebsite: str(v.instagramWebsite),
    instagramMentionsSkida: v.instagramMentionsSkida === true,
    instagramFetchedAt: toIsoOrNull(v.instagramFetchedAt),
    igAccountType:
      v.igAccountType === 'professional' || v.igAccountType === 'personal' ? v.igAccountType : '',
  }
}

/**
 * Update one person.
 *
 * Lives beside the events repository rather than in an ambassadors one because
 * `skidaCrmPeople` is a single collection with a single subscription — two
 * repositories over one collection would mean two live listeners and two
 * mapping functions that could drift.
 *
 * Whitelisted rather than a blind spread: `role` and `linkedLeadId` are set by
 * the importers and the CRM-user sync, and an edit screen must not be able to
 * turn an ambassador into staff by accident.
 */
/**
 * Remove a person.
 *
 * A hard delete, and worth knowing what it leaves behind: posts hold ambassador
 * ids in `linkedAmbassadorIds`, and mentions and comments resolve by Instagram
 * handle against the live records. After this, a post's linked name renders
 * empty and a tagged post falls back to "not recognised" — both already handle a
 * missing record, because a handle moving between records was always possible.
 *
 * `status: 'ended'` exists for the softer case and is usually the right one: it
 * keeps the history readable. This is for a record that should never have been
 * created.
 */
export async function deletePerson(id: string): Promise<void> {
  await deleteDoc(doc(db, EVENT_COLLECTIONS.people, id))
}

export async function updatePerson(id: string, patch: Partial<Person>): Promise<void> {
  const { doc, setDoc, serverTimestamp } = await import('firebase/firestore')
  const allowed: (keyof Person)[] = [
    'name', 'email', 'phone', 'homeArea', 'postalAddress', 'tshirt', 'oneLiner', 'website',
    'status', 'compensation', 'campaignCode', 'contentDelivered', 'driveFolderUrl',
    'instagram', 'facebook', 'tiktok', 'youtube', 'linkedin',
    // instagramFollowers and the profile fields beside it are deliberately not
    // here. They are written by the nightly Business Discovery pass, and a save
    // from the editor carrying a stale copy would silently undo it.
    'active', 'countries', 'languages', 'maxTravelKm',
  ]
  /**
   * A field this list does not name is an error, not something to drop.
   *
   * It used to be dropped. `driveFolderUrl` was added to the type, the editor
   * and the ambassador's own screen on 29 Aug and left out of this list, so the
   * dialog sent it, this function discarded it, and the save reported success
   * having changed nothing -- which is the least useful way for a save to fail.
   * The only caller builds its patch as an explicit object, so an unknown key
   * is always a field somebody forgot to add here.
   */
  const unknown = Object.keys(patch).filter((k) => !allowed.includes(k as keyof Person))
  if (unknown.length) {
    throw new Error(
      `updatePerson cannot write ${unknown.join(', ')} -- add the field to ` +
        `\`allowed\` in eventsRepository.ts, or stop sending it.`,
    )
  }

  const out: Record<string, unknown> = {}
  for (const k of allowed) if (k in patch) out[k] = patch[k]
  out.updatedAt = serverTimestamp()
  await setDoc(doc(db, EVENT_COLLECTIONS.people, id), out, { merge: true })
}

/**
 * Add somebody to the programme — decision 71.
 *
 * Ambassadors have always arrived through the interest form or the importer,
 * and the screen had no way to add one. That is the defect the standing rule
 * names: a list with no create button is always a defect, and no screen is
 * allowed to declare itself read-only instead.
 *
 * `source` records that this one was typed in rather than harvested, because
 * the rest of the programme carries where it came from and a blank would read
 * as an import that lost the field.
 */
export async function createPerson(patch: Partial<Person>): Promise<string> {
  const { addDoc, collection, serverTimestamp } = await import('firebase/firestore')
  const ref = await addDoc(collection(db, EVENT_COLLECTIONS.people), {
    ...patch,
    role: 'ambassador',
    source: 'added by hand',
    active: true,
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
  })
  return ref.id
}

export function subscribeToEvents(
  onData: (d: { events: CrmEvent[]; assignments: EventAssignment[]; people: Person[] }) => void,
  onError: (e: Error) => void,
): () => void {
  let events: CrmEvent[] = []
  let assignments: EventAssignment[] = []
  let people: Person[] = []
  // Three live queries feeding one callback. Nothing is emitted until all three
  // have answered once, because coverage is meaningless without people and
  // assignments — a first paint from events alone would show every event as
  // uncovered and then correct itself, which reads as a bug.
  const seen = { events: false, assignments: false, people: false }
  const emit = () => {
    if (seen.events && seen.assignments && seen.people) onData({ events, assignments, people })
  }

  const unsubs = [
    onSnapshot(
      collection(db, EVENT_COLLECTIONS.events),
      (s) => {
        events = s.docs.map(mapEvent)
        seen.events = true
        emit()
      },
      (e) => onError(e as Error),
    ),
    onSnapshot(
      collection(db, EVENT_COLLECTIONS.assignments),
      (s) => {
        assignments = s.docs.map(mapAssignment)
        seen.assignments = true
        emit()
      },
      (e) => onError(e as Error),
    ),
    onSnapshot(
      collection(db, EVENT_COLLECTIONS.people),
      (s) => {
        people = s.docs.map(mapPerson)
        seen.people = true
        emit()
      },
      (e) => onError(e as Error),
    ),
  ]
  return () => unsubs.forEach((u) => u())
}

export interface ScoredEvent extends CrmEvent {
  /** Recomputed here rather than read from the document. */
  computedCoverage: number | null
  computedScore: number | null
  computedTier: CrmEvent['tier']
  computedPerKEur: number | null
  /** Empty when scored; otherwise says why it was not. */
  skipped: string
  assignments: EventAssignment[]
  /** Total of the assignment costs, which is what `costEstimateEur` means. */
  cost: number | null
}

/**
 * Score every event against the current people and assignments.
 *
 * Coverage needs to know whether a person is already committed elsewhere on
 * the same dates, so each assignment is tagged with whether its own event
 * clashes with the one being scored before the pure scorer sees it.
 */
export function scoreEvents(
  events: CrmEvent[],
  assignments: EventAssignment[],
  people: Person[],
): ScoredEvent[] {
  const byId = new Map(events.map((e) => [e.id, e]))
  return events.map((event) => {
    const tagged = assignments.map((a) => {
      const other = byId.get(a.eventId)
      const clash =
        !!other &&
        !!event.startDate &&
        !!other.startDate &&
        event.startDate <= (other.endDate || other.startDate) &&
        other.startDate <= (event.endDate || event.startDate)
      return { ...a, overlapsEvent: clash }
    })
    const mine = tagged.filter((a) => a.eventId === event.id)
    const costs = mine.map((a) => a.costEur).filter((c): c is number => c !== null)
    const cost = costs.length ? costs.reduce((x, y) => x + y, 0) : event.costEstimateEur

    const coverage = coverageFor({ event, people, assignments: tagged })
    const s = scoreEvent({
      relevance: event.relevance,
      reach: event.reach,
      coverage,
      costEstimateEur: cost,
      stage: event.stage,
      sourceVerified: event.sourceVerified,
    })
    return {
      ...event,
      computedCoverage: coverage,
      computedScore: s.eventScore,
      computedTier: s.tier,
      computedPerKEur: s.scorePerKEur,
      skipped: s.skipped,
      assignments: mine,
      cost,
    }
  })
}

/**
 * Sales-owned fields only.
 *
 * The derived fields are deliberately absent from this signature: the spec puts
 * them under the scoring pass, and a form that could write them would make the
 * score editable, which is the one thing it must not be.
 */
export async function updateEvent(
  id: string,
  patch: Partial<
    Pick<
      CrmEvent,
      | 'stage' | 'ownerId' | 'ownerName' | 'outreachBy' | 'trackingCode'
      | 'budgetNok' | 'actualSpendNok' | 'registered' | 'attendees'
      | 'nextAction' | 'notes' | 'relevance' | 'reach' | 'startDate' | 'endDate'
      | 'sourceVerified' | 'venue' | 'website' | 'instagram'
    >
  >,
): Promise<void> {
  await setDoc(doc(db, EVENT_COLLECTIONS.events, id), patch, { merge: true })
}

/**
 * Put an event in the book by hand — decision 71, built after decision 64.
 *
 * Every event until now arrived through `scripts/importEvents.ts`, which is why
 * this screen had no create button: the book was a harvest, and nothing on it
 * was ever typed. That made it the clearest case of the standing rule — a list
 * with no create button is always a defect.
 *
 * The harvest fields are left empty rather than guessed. `sourceVerified` says
 * where a row came from, and "added by hand" is the truthful answer for one
 * nobody harvested; a blank there would read as an import that lost the field.
 * The score stays uncomputed until somebody sets relevance and reach, which is
 * the same state an unassessed import is in.
 */
export async function createEvent(patch: Partial<CrmEvent>): Promise<string> {
  const { addDoc, collection, serverTimestamp } = await import('firebase/firestore')
  const ref = await addDoc(collection(db, EVENT_COLLECTIONS.events), {
    ...patch,
    stage: patch.stage ?? 'unqualified',
    sourceVerified: 'added by hand',
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
  })
  return ref.id
}

export async function upsertAssignment(a: Omit<EventAssignment, 'id'>): Promise<void> {
  // Deterministic id: one row per person per event, so assigning twice edits
  // rather than duplicates.
  const id = `${a.eventId}__${a.personId}`
  await setDoc(doc(db, EVENT_COLLECTIONS.assignments, id), { ...a, id }, { merge: true })
}

export async function removeAssignment(eventId: string, personId: string): Promise<void> {
  await deleteDoc(doc(db, EVENT_COLLECTIONS.assignments, `${eventId}__${personId}`))
}
